import { InputText } from 'component-library/index';
import React, { useCallback } from 'react';
import { memo } from 'react';
import { useField } from '../../../providers/ModernAcquisitionProvider/useField';
import { checkExistsDiacriticSpecialCharacters, regexValidation } from '../../../utils/cob-utils';
import { TErrorType } from '../../../providers/ModernAcquisitionProvider/types';
import { generateFieldErrorMessage } from '../../../utils/error-message';

export interface INameFieldWithProviderProps {
    fieldKey: 'firstName' | 'lastName' | 'authorizedUserFirstName' | 'authorizedUserLastName';
    name: 'First Name' | 'Last Name';
    label?: string;
    id?: string;
    disabled?: boolean;
    errorName?: string;
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
}

const MemoizedInputText = memo(InputText);

export const NameFieldWithProvider = memo(function NameFieldWithProvider(props: INameFieldWithProviderProps & React.HTMLAttributes<HTMLInputElement>, ...rest) {

    const { field, updateField } = useField(props.fieldKey);
    const regex = regexValidation();

    const validateFirstAndLastName = useCallback((toValidate: Readonly<string>): boolean => { 
        const regexKey = props.fieldKey.toLowerCase().includes('firstname') ? 'first_name' : 'last_name';
        const trimmedValue = toValidate?.trim();

        if (!trimmedValue) {
            return false;
        }

        if (!regex.allow[regexKey].test(trimmedValue)) {
            return false;
        }

        if (regex.disallow[regexKey].test(trimmedValue)) {
            return false;
        }
        
        if (checkExistsDiacriticSpecialCharacters(trimmedValue)) {
            return false;
        }
        
        return true;
    }, [
        props.name,
    ]);

    const formattedFirstAndLastName = (nextValue: string): string => {
        if (!nextValue) {
            return '';
        }

        if (nextValue?.length > 60) {
            nextValue = nextValue.substring(0, 60);
        }

        if (nextValue) {
            let format = '';
            const isAposthropheRepeated = nextValue.match(/'/g)?.length === 2 && nextValue.substring(nextValue.length - 1) === '\'';

            if (isAposthropheRepeated) {
                format = nextValue.substring(0, nextValue.length - 1);
            } else {
                if (props.fieldKey === 'lastName' || props.fieldKey === 'authorizedUserLastName') {
                    format = nextValue
                        .replace(/[^a-zA-Z-'\s]/g, '')
                        .replace(/^'/g, '')
                        .replace(/'+/g, '\'');
                } else {
                    format = nextValue
                        .replace(/[^a-zA-Z-\s]/g, '');
                }
                format = format
                    .replace(/^-/g, '')
                    .replace(/-{2,}/g, '-')
                    .replace(/\s{2,}/g, ' ');
            }
            return format;
        }
        return '';
    };


    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const formatted = formattedFirstAndLastName(event.target.value);

        if (field.value !== event.target.value) {
            updateField({ ...field, state: 'neutral', value: formatted });
        } else {
            updateField({ ...field, value: formatted });
        }
    }

    const handleBlur = (): void => {
        const trimmedValue = (field.value as string).trim();
        const isValid = validateFirstAndLastName(trimmedValue);

    if (isValid) {
            updateField({ ...field, state: 'success', errorType: 'None' })
        } else {
            const error: TErrorType = trimmedValue.length === 0 ? 'requiredField' : 'invalidInput';
            updateField({ ...field, state: 'error', errorType: error })
        }

        //Incase someone wants to extend blur functionality.
        props.onBlurCallback && props.onBlurCallback();
    }

    const handleFocus = (): void => {
        if (field.state === 'success') {
            updateField({ ...field, state: 'neutral' })
        }

        //Incase someone wants to extend focus functionality.
        props.onFocusCallback && props.onFocusCallback();
    }

    return (
        <MemoizedInputText
            required
            name={props.name}
            label={props.label || props.name}
            id={props.id}
            value={field.value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            errorMessage={generateFieldErrorMessage(props.errorName || props.name, !field.value ? 'missingValue' : null)}
            state={field.state}
            maxLength={30}
            disabled={props.disabled}
            {...rest}
        />

    )

});

export default NameFieldWithProvider;